// src/utils/auth.js
// Centralized helpers for auth persistence + profile refresh.

import { clearAccessOverridesForUser, clearAllAccessOverrides } from "./accessPreferenceStore";
import { dispatchUserReadyEvent } from "./userEvents";

const AUTH_KEY = "pp_auth";
const USER_KEY = "pp_user";
const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const LOGOUT_TIMEOUT_MS = 5000;

export const ROLES = ["Admin", "Dispatcher", "Drayage", "Warehouse", "Overview"];

export function getAuth() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function readAuthSafe() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!(parsed && parsed.ok === true && parsed.token)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function isSignedIn() {
  return !!readAuthSafe();
}

export function getCurrentUser() {
  const auth = getAuth();
  if (auth?.user) return auth.user;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function hasRole(role) {
  const user = getCurrentUser();
  if (!user?.role) return false;
  return String(user.role).toLowerCase() === String(role).toLowerCase();
}

export function inAnyRole(...roles) {
  const user = getCurrentUser();
  if (!user?.role) return false;
  const mine = String(user.role).toLowerCase();
  return roles.some((role) => String(role).toLowerCase() === mine);
}

function persistAuthPayload(token, user, { resetOverrides = false } = {}) {
  const payload = { ok: true, token, user: user || null };
  localStorage.setItem(AUTH_KEY, JSON.stringify(payload));
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (resetOverrides) {
      clearAccessOverridesForUser(user);
    }
  }
  dispatchUserReadyEvent();
}

export function saveAuth(token, user) {
  persistAuthPayload(token, user, { resetOverrides: true });
}

export function updateAuthUser(updater, { resetOverrides = false } = {}) {
  const auth = getAuth() || { ok: true };
  const currentUser = auth.user || {};
  const nextUser =
    typeof updater === "function" ? updater({ ...currentUser }) : { ...currentUser, ...(updater || {}) };
  const token = auth.token;
  if (!token) throw new Error("not_signed_in");
  persistAuthPayload(token, nextUser, { resetOverrides });
  return nextUser;
}

export async function signOut({ revokeRemote = true } = {}) {
  const hadAuth = localStorage.getItem(AUTH_KEY) !== null;
  const hadUser = localStorage.getItem(USER_KEY) !== null;
  const auth = readAuthSafe();
  const user = getCurrentUser();
  const hadToken = Boolean(auth?.token);
  const result = {
    hadToken,
    remoteAttempted: false,
    remoteRevoked: false,
  };

  if (!hadAuth && !hadUser) return result;

  try {
    if (revokeRemote && hadToken) {
      result.remoteAttempted = true;
      const controller = typeof AbortController === "function" ? new AbortController() : null;
      let timeoutId;
      try {
        const request = fetch(`${API_BASE}/api/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${auth.token}` },
          ...(controller ? { signal: controller.signal } : {}),
        });
        const timeout = new Promise((resolve) => {
          timeoutId = setTimeout(() => {
            if (controller) controller.abort();
            resolve(null);
          }, LOGOUT_TIMEOUT_MS);
        });
        const response = await Promise.race([request, timeout]);
        result.remoteRevoked = Boolean(response?.ok);
      } catch {
        result.remoteRevoked = false;
      } finally {
        clearTimeout(timeoutId);
      }
    }
  } finally {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(USER_KEY);
    if (user) clearAccessOverridesForUser(user);
    else clearAllAccessOverrides();
    dispatchUserReadyEvent();
  }

  return result;
}

export function isAdmin() {
  const user = getCurrentUser();
  return (user?.role || "").toLowerCase() === "admin";
}

export async function refreshUserProfile() {
  const auth = readAuthSafe();
  if (!auth?.token) throw new Error("not_signed_in");
  const headers = {
    Authorization: `Bearer ${auth.token}`,
    "X-User-Email": auth.user?.email || "",
    "X-User-Role": auth.user?.role || "",
  };
  const resp = await fetch(`${API_BASE}/api/me`, { headers });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.ok === false) {
    if (resp.status === 401) signOut({ revokeRemote: false });
    throw new Error(data?.error || "profile_failed");
  }
  updateAuthUser((user) => ({
    ...user,
    role: data.role || user.role,
    default_page_id: data.user_default_page_id || null,
    role_default_page_id: data.role_default_page_id || null,
    landing_route: data.landing_route || user.landing_route,
  }));
  return data;
}

const authUtils = {
  ROLES,
  getAuth,
  readAuthSafe,
  isSignedIn,
  getCurrentUser,
  hasRole,
  inAnyRole,
  saveAuth,
  updateAuthUser,
  signOut,
  isAdmin,
  refreshUserProfile,
};

export default authUtils;
